import { useToggle } from 'ahooks'
import { Button, Col, DatePicker, Form, FormInstance, Input, Modal, Row, Select } from 'antd'
import { useAtom } from 'jotai'
import { toNumber } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { DepartmentTreeTransfer, isIdPureNumber } from '@/components/department-tree-transfer.component'
import { onDepartmentUserCheck } from '@/components/department-tree-with-users.component'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { useOnSetSpecialProjectCache } from '@/components/special-project-local-storage-modal'
import { PriorityEnum, SpecialProjectFormLocalStorageKey } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { departmentsTreeAtom } from '../department'
import { DepartmentUser } from '../project/project-create/project-form.component'
import { userInfoAtom, usersAtom } from '../user'
import { Devlop, DevlopClassification } from './devlop.schema'

const { RangePicker } = DatePicker

const validateMessages = {
  required: '${label}是必选填项!',
}

const DevlopClassificationStartIndex = 8 as const

export const classificationOptionsRaw = Object.entries(DevlopClassification)
  .filter(([value]) => Number(value) >= DevlopClassificationStartIndex)
  .map(([value, key]) => ({
    value: Number(value),
    label: key,
  }))

// 调整展示顺序
export const classificationOptions = classificationOptionsRaw.slice(1).concat(classificationOptionsRaw.slice(0, 1))

export type DevlopFromValues = {
  cycleId: number
  departmentId: number
  email: string
  name: string
  range: [string, string]
  requirementTypes: string
}

export const DevlopForm: React.FC<{
  form: FormInstance<any>
  devlop?: Devlop
  onSubmit: (data: DevlopFromValues) => Promise<Devlop>
}> = ({ devlop, form, onSubmit }) => {
  const { t } = useTranslation()
  const [departments] = useAtom(departmentsTreeAtom)
  const [userInfo] = useAtom(userInfoAtom)
  const { onSetCache, setFormCache } = useOnSetSpecialProjectCache(SpecialProjectFormLocalStorageKey.DEVLOP)

  const [modalVisible, { toggle: modalToggle }] = useToggle()

  const [departmentUsers, setDepartmentUsers] = React.useState<DepartmentUser[]>([])

  const readPermissions = devlop?.readPermissions || []
  React.useEffect(() => {
    const defaultCheckedKeys =
      readPermissions.length > 0
        ? readPermissions
            .map((permission) => {
              // departmentId
              if (isIdPureNumber(permission)) {
                return Number(permission)
              }
              // userId
              const user = users.find((user) => user.email === permission)
              return user ? `${user.departmentIds[0]}-${user.email}` : null
            })
            .filter(Boolean)
            .map((id) => id!)
        : []
    onDepartmentUserCheck(defaultCheckedKeys, departments, users, setDepartmentUsers)
  }, [])

  const [user] = useAtom(userInfoAtom)
  const [loading, setLoading] = React.useState(false)
  React.useEffect(() => {
    if (!devlop) {
      form.setFieldsValue({
        email: user.email,
      })
    } else {
      const data = {
        name: devlop.name,
        email: devlop.email,
        range: [devlop.startDate ? moment(devlop.startDate) : null, devlop.endDate ? moment(devlop.endDate) : null],
        classification: devlop.classification,
        challenge: devlop.challenge,
        priority: devlop.priority,
      }
      form.setFieldsValue(data)
    }
  }, [devlop])

  const [users] = useAtom(usersAtom)

  const onValuesChange = (fieldData: any) => {
    // 创建时，缓存数据
    if (!devlop) {
      onSetCache(fieldData)
    }
    const { email } = fieldData
    if (email) {
      form.setFieldsValue({ departmentId: users.find((u) => u.email === email)?.departmentIds[0] })
    }
  }

  const onFinish = () => {
    const values = form.getFieldsValue()
    // eslint-disable-next-line no-console
    const { range, ...fields } = values
    const data = {
      ...fields,
      startDate: range ? range[0].format('YYYY-MM-DD') : undefined,
      endDate: range ? range[1].format('YYYY-MM-DD') : undefined,
    }
    setLoading(true)
    onSubmit(data).finally(() => {
      setLoading(false)
      setFormCache(undefined)
    })
  }

  return (
    <>
      {!devlop ? (
        <Row>
          <Col className='py-8'>
            <h2>{t('devlop.createDevlop')}</h2>
          </Col>
        </Row>
      ) : null}
      <Form
        preserve={false}
        onFinish={onFinish}
        form={form}
        layout='vertical'
        validateMessages={validateMessages}
        onValuesChange={onValuesChange}
      >
        <Form.Item label={t('devlop.name')} required name='name' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('devlop.owner')} required name='email' rules={[{ required: true }]}>
          <UsersSearch allowClear={false} />
        </Form.Item>
        <Form.Item
          label={t('devlop.detail.classification')}
          required
          name='classification'
          rules={[{ required: true }]}
        >
          <Select placeholder={t('devlop.detail.classification')} options={classificationOptions}></Select>
        </Form.Item>
        <Form.Item label={t('devlop.timePeriod')} required name='range' rules={[{ required: true }]}>
          <RangePicker format='YYYY-MM-DD' />
        </Form.Item>
        {!devlop ? (
          <>
            <Form.Item
              valuePropName='content'
              tooltip={t('devlop.form.background.tooltip')}
              label={t('devlop.detail.background')}
              name='background'
              trigger='onChange'
              rules={[
                {
                  required: true,
                  validator: async (_, value) =>
                    !removeHtmlTag(value).trim()
                      ? Promise.reject(`${t('devlop.detail.background')}是必选填项!`)
                      : Promise.resolve(),
                },
              ]}
            >
              <Editor className={'editorBorder'} disableAutoSaveStyle />
            </Form.Item>
            <Form.Item
              valuePropName='content'
              tooltip={t('devlop.form.goal.tooltip')}
              label={t('devlop.detail.goal')}
              name='goal'
              trigger='onChange'
              rules={[
                {
                  required: true,
                  validator: async (_, value) =>
                    !removeHtmlTag(value).trim()
                      ? Promise.reject(`${t('devlop.detail.goal')}是必选填项!`)
                      : Promise.resolve(),
                },
              ]}
            >
              <Editor className={'editorBorder'} disableAutoSaveStyle />
            </Form.Item>
            <Form.Item
              valuePropName='content'
              tooltip={t('devlop.form.standard.tooltip')}
              label={t('devlop.detail.standard')}
              name='standard'
              trigger='onChange'
              rules={[
                {
                  required: true,
                  validator: async (_, value) =>
                    !removeHtmlTag(value).trim()
                      ? Promise.reject(`${t('devlop.detail.standard')}是必选填项!`)
                      : Promise.resolve(),
                },
              ]}
            >
              <Editor className={'editorBorder'} disableAutoSaveStyle />
            </Form.Item>
            <Form.Item
              label={t('devlop.detail.challenge')}
              tooltip={t('resource.challengeTooltip')}
              valuePropName='content'
              trigger='onChange'
              name='challenge'
              rules={[
                {
                  required: true,
                  validator: async (_, value) =>
                    !removeHtmlTag(value).trim()
                      ? Promise.reject(`${t('resource.challenge')}是必选填项!`)
                      : Promise.resolve(),
                },
              ]}
            >
              <Editor disableAutoSaveStyle className={'editorBorder'} />
            </Form.Item>
          </>
        ) : null}
        <Form.Item
          label={
            <div>
              <span>{t('devlop.detail.priority')}</span>
              <PriorityPopoverDescription />
            </div>
          }
          name='priority'
          rules={[{ required: true }]}
        >
          <Select
            options={Object.keys(PriorityEnum)
              .filter((v) => isNaN(toNumber(v)))
              .map((key) => ({
                label: t(`form.priority.${key}`),
                value: PriorityEnum[key],
              }))}
          />
        </Form.Item>
        <Form.Item>
          <Button hidden={!!devlop} loading={loading} type='primary' htmlType='submit'>
            {t('create')}
          </Button>
        </Form.Item>
      </Form>
      <Modal
        width={700}
        visible={modalVisible}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => modalToggle()}
        destroyOnClose
      >
        <DepartmentTreeTransfer
          users={users}
          departments={departments}
          userInfo={userInfo}
          defaultPermissions={readPermissions}
          departmentUsers={departmentUsers}
          setDepartmentUsers={setDepartmentUsers}
        />
      </Modal>
    </>
  )
}
